// Is the Personal side hidden right now? (PRD 50 §5)
//
// The preference alone does not answer this. It is honoured ONLY while the
// Business section is actually rendering — otherwise a customer whose Pro
// subscription lapses signs in to an empty sidebar with one Settings row,
// having lost the personal app they never cancelled.
//
// The stored preference is not cleared when that happens; it is not HONOURED,
// which is different. Restore the subscription and their choice is still
// theirs, untouched. A flag that erased itself on a billing hiccup would be a
// support conversation about a setting nobody changed.
//
// WHEN IN DOUBT, SHOW THE PERSONAL APP. Showing something a customer does not
// want is a mild annoyance; hiding the only thing they can reach is an outage.

import { useAuth } from '../contexts/AuthContext';
import { useAppPreferences } from '../contexts/AppPreferencesContext';
import { useBusinessAccountingFlag } from './useBusinessDataMode';
import { isDemo } from '../config/demo';

/**
 * Is there a Business section to fall back on? Null while still deciding.
 *
 * This is the SAME condition WebNavigation applies to the Business section
 * itself, deliberately expressed once: the rule "hide personal only when
 * business shows" is only true if both sides read one answer.
 */
export function useBusinessSectionAvailable(): boolean | null {
  const { isPro } = useAuth();
  const businessLive = useBusinessAccountingFlag();

  // Pro is known synchronously, so there is nothing to wait for.
  if (isPro) return true;
  return businessLive === null ? null : businessLive;
}

/**
 * Should the Personal side be hidden? Null while the answer is still being
 * decided — callers that can wait (the landing redirect) should; callers that
 * cannot (the sidebar) treat null as "show".
 */
export function usePersonalSideHidden(): boolean | null {
  const { personalHidden } = useAppPreferences();
  const businessAvailable = useBusinessSectionAvailable();

  // Nobody has asked for anything: no need to resolve the business question,
  // and no chance of a redirect that waits on a fetch it does not need.
  // The Live sample hides it whatever the seed says (PRD 117): its shared
  // user's preference is frozen, and a sample of personal finance is not what
  // anybody came from a business page to see.
  if (!personalHidden && !isDemo()) return false;
  if (businessAvailable === null) return null;
  return businessAvailable;
}
